import React, { useMemo } from 'react';
import useAudioCapture from '../hooks/useAudioCapture';
import { calcularLAeq, calcularMaximo, calcularMinimo, EXEMPLOS_NIVEIS } from '../utils/calculosSonoros';

/**
 * Componente de Medição em Tempo Real
 * Exibe o medidor de dB e permite capturar amostras
 */
const Medicao = ({ calibrationOffset, onMeasurementComplete }) => {
  const {
    isRecording,
    currentDb,
    peakDb,
    samples,
    frequencyData,
    error,
    startRecording,
    stopRecording,
    resetSamples
  } = useAudioCapture(calibrationOffset);

  // Calcula estatísticas das amostras
  const stats = useMemo(() => {
    if (samples.length === 0) return null;
    
    const valores = samples.map(s => s.db);
    return {
      laeq: calcularLAeq(valores),
      max: calcularMaximo(valores),
      min: calcularMinimo(valores),
      count: valores.length,
      duration: samples.length > 1 
        ? Math.round((samples[samples.length - 1].timestamp - samples[0].timestamp) / 1000)
        : 0
    };
  }, [samples]);

  // Determina a cor do medidor baseado no nível
  const getMeterColor = (db) => {
    if (db < 50) return '#4caf50'; // Verde - seguro
    if (db < 70) return '#8bc34a'; // Verde claro
    if (db < 85) return '#ffeb3b'; // Amarelo - atenção
    if (db < 100) return '#ff9800'; // Laranja - cuidado
    return '#f44336'; // Vermelho - perigoso
  };

  // Determina a descrição do nível
  const getLevelDescription = (db) => {
    const exemplo = EXEMPLOS_NIVEIS.find(e => db <= e.nivel + 5 && db >= e.nivel - 5);
    return exemplo ? exemplo.descricao : '';
  };

  // Finaliza medição e retorna dados
  const handleStopAndSave = () => {
    const finalSamples = stopRecording();
    if (onMeasurementComplete && finalSamples.length > 0) {
      const valores = finalSamples.map(s => s.db);
      onMeasurementComplete({
        valores,
        frequencyData,
        samples: finalSamples,
        stats: {
          laeq: calcularLAeq(valores),
          max: calcularMaximo(valores),
          min: calcularMinimo(valores),
          count: valores.length
        }
      });
    }
  };

  // Porcentagem para o medidor visual (0-140 dB)
  const meterPercentage = Math.min(100, (currentDb / 140) * 100);
  const peakPercentage = Math.min(100, (peakDb / 140) * 100);

  return (
    <div className="medicao-container">
      <div className="medicao-header">
        <h3>📊 Medição em Tempo Real</h3>
        {calibrationOffset !== 0 && (
          <span className="calibration-badge">
            Calibrado ({calibrationOffset > 0 ? '+' : ''}{calibrationOffset} dB)
          </span>
        )}
      </div>

      {error && (
        <div className="medicao-error">
          ❌ {error}
        </div>
      )}

      {/* Display principal de dB */}
      <div className="db-display">
        <div className="db-value" style={{ color: getMeterColor(currentDb) }}>
          {currentDb.toFixed(1)}
        </div>
        <div className="db-unit">dB(A)</div>
        <div className="db-description">{getLevelDescription(currentDb)}</div>
      </div>

      {/* Medidor visual tipo barra */}
      <div className="meter-container">
        <div className="meter-scale">
          {[0, 20, 40, 60, 80, 100, 120, 140].map(val => (
            <span key={val} className="scale-mark" style={{ left: `${(val / 140) * 100}%` }}>
              {val}
            </span>
          ))}
        </div>
        <div className="meter-bar">
          <div 
            className="meter-fill"
            style={{ 
              width: `${meterPercentage}%`,
              backgroundColor: getMeterColor(currentDb)
            }}
          />
          <div 
            className="meter-peak"
            style={{ left: `${peakPercentage}%` }}
          />
        </div>
        <div className="meter-zones">
          <div className="zone safe" style={{ width: '36%' }} />
          <div className="zone caution" style={{ width: '25%' }} />
          <div className="zone warning" style={{ width: '18%' }} />
          <div className="zone danger" style={{ width: '21%' }} />
        </div>
      </div>

      {/* Estatísticas da sessão */}
      {stats && (
        <div className="stats-container">
          <div className="stat-item">
            <span className="stat-label">LAeq</span>
            <span className="stat-value">{stats.laeq.toFixed(1)} dB</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Máximo</span>
            <span className="stat-value">{stats.max.toFixed(1)} dB</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Mínimo</span>
            <span className="stat-value">{stats.min.toFixed(1)} dB</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Pico</span>
            <span className="stat-value peak">{peakDb.toFixed(1)} dB</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Amostras</span>
            <span className="stat-value">{stats.count}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Duração</span>
            <span className="stat-value">{stats.duration}s</span>
          </div>
        </div>
      )}

      {/* Gráfico de frequências (simplificado) */}
      {isRecording && frequencyData.length > 0 && (
        <div className="frequency-display">
          <h4>Espectro de Frequência</h4>
          <div className="frequency-bars">
            {frequencyData.slice(0, 20).map((band, index) => (
              <div key={index} className="freq-bar-container">
                <div 
                  className="freq-bar"
                  style={{ 
                    height: `${Math.min(100, (band.db / 100) * 100)}%`,
                    backgroundColor: getMeterColor(band.db)
                  }}
                />
                <span className="freq-label">
                  {band.freq >= 1000 ? `${band.freq/1000}k` : band.freq}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mini gráfico de histórico */}
      {samples.length > 10 && (
        <div className="history-graph">
          <h4>Histórico</h4>
          <div className="history-container">
            <svg viewBox="0 0 100 40" preserveAspectRatio="none">
              <polyline
                fill="none"
                stroke={getMeterColor(currentDb)}
                strokeWidth="0.5"
                points={
                  samples.slice(-100).map((s, i, arr) => 
                    `${(i / arr.length) * 100},${40 - (s.db / 140) * 40}`
                  ).join(' ')
                }
              />
            </svg>
          </div>
        </div>
      )}

      {/* Controles */}
      <div className="medicao-controls">
        {!isRecording ? (
          <>
            <button 
              className="btn btn-primary btn-large"
              onClick={startRecording}
            >
              🎤 Iniciar Medição
            </button>
            {samples.length > 0 && (
              <button 
                className="btn btn-secondary"
                onClick={resetSamples}
              >
                ↺ Limpar
              </button>
            )}
          </>
        ) : (
          <>
            <button 
              className="btn btn-danger btn-large"
              onClick={stopRecording}
            >
              ⏹ Parar
            </button>
            <button 
              className="btn btn-success"
              onClick={handleStopAndSave}
            >
              ✓ Salvar Medição
            </button>
          </>
        )}
      </div>

      {/* Indicador de status */}
      <div className={`recording-status ${isRecording ? 'active' : ''}`}>
        {isRecording ? (
          <>
            <span className="recording-dot" />
            Gravando...
          </>
        ) : (
          'Pronto para medir'
        )}
      </div>
    </div>
  );
};

export default Medicao;
